import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Typography } from '@mui/material';
import DialogComponent from '../../component/dialog/dialog.component';
import CommonInput from '../../component/form/common-input.component';
import type  { SelectOption } from '../../component/form/base-input.component';
import i18n from '../../i18n';
import { ITEM_TYPE, type MenuDish } from '../../models';
import {
    getMenuLibraryDetailDishRowKey,
    getMenuLibraryFilterDishIdDisplay,
    type MenuLibraryManagementState,
    type DishFilterQuickPreset,
} from './menu-library-management-state';
import { ReactComponent as FilterTitleIcon } from '../../static/images/svg/filter.svg';
import './scss/menu-library-dish-filter-dialog.style.scss';

const QUICK_CHIPS: { id: DishFilterQuickPreset; labelKey: string }[] = [
    { id: 'all', labelKey: 'menu_library_filter_chip_all' },
    { id: 'combo', labelKey: 'menu_library_filter_chip_combo' },
    { id: 'seasonal', labelKey: 'menu_library_filter_chip_seasonal' },
    { id: 'weight', labelKey: 'menu_library_filter_chip_weight' },
    { id: 'new', labelKey: 'menu_library_filter_chip_new' },
    { id: 'signature', labelKey: 'menu_library_filter_chip_signature' },
    { id: 'no_discount', labelKey: 'menu_library_filter_chip_no_discount' },
];

type Props = {
    state: MenuLibraryManagementState;
};

/**
 * 菜单库详情 — 菜品筛选弹窗（Figma 3192:52996 / 3330:25007）
 */
export const MenuLibraryDishFilterDialog: React.FC<Props> = observer(({ state }) => {
    const categoryItems: SelectOption[] = useMemo(() => {
        const all: SelectOption = { value: 'all', label: i18n.t('menu_library_filter_category_all') };
        const rest = ITEM_TYPE.map((it) => ({
            value: String(it.value),
            label: it.label,
        }));
        return [all, ...rest];
    }, []);

    const imageItems: SelectOption[] = useMemo(
        () => [
            { value: 'all', label: i18n.t('menu_library_filter_image_all') },
            { value: 'uploaded', label: i18n.t('menu_library_filter_image_uploaded') },
            { value: 'not_uploaded', label: i18n.t('menu_library_filter_image_not_uploaded') },
        ],
        [],
    );

    const printItems: SelectOption[] = useMemo(() => {
        const entries = Object.entries(state.printGroupMap)
            .map(([k, label]) => ({ num: Number(k), label }))
            .filter((x) => Number.isFinite(x.num) && x.num !== -1)
            .sort((a, b) => a.num - b.num);
        const opts: SelectOption[] = [{ value: 'all', label: i18n.t('menu_library_filter_print_all') }];
        for (const e of entries) {
            opts.push({ value: String(e.num), label: e.label || String(e.num) });
        }
        return opts;
    }, [state.printGroupMap]);

    const previewRows = state.getDishFilterPreviewDishes();

    return (
        <DialogComponent
            className="menu-library-dish-filter-dialog__dialog-root"
            open={state.isDishFilterDialogOpen}
            onClose={() => state.closeDishFilterDialog()}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(1000px, 96vw)',
                    maxWidth: '1000px',
                    maxHeight: 'min(720px, calc(100vh - 32px))',
                    margin: '16px auto',
                    /** Figma 3192:52996：内容区白底，描边主题色 300 */
                    background: '#ffffff',
                    backgroundColor: '#ffffff',
                    border: '2px solid var(--fill-primary-300)',
                    /** 与全局 Dialog 一致 16px，并裁剪子元素避免直角超出圆角 */
                    borderRadius: '16px',
                    overflow: 'hidden',
                },
            }}
            sx={{
                '& .MuiDialog-container > .MuiPaper-root': {
                    background: '#ffffff',
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    overflow: 'hidden',
                },
                '& .MuiDialogTitle-root': {
                    backgroundColor: '#ffffff',
                },
                '& .MuiDialogContent-root': {
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: '8px',
                    backgroundColor: '#ffffff',
                },
                /** Figma：底部条 #eee；外边距与纸边留白 */
                '& .MuiDialogActions-root.dialog-footer': {
                    background: '#eee',
                    backgroundColor: '#eee',
                    padding: '8px 24px',
                    margin: '0 16px 16px',
                    justifyContent: 'flex-end',
                },
            }}
            defaultHeaderProps={{
                title: i18n.t('menu_library_filter_dialog_title'),
                TitleIcon: (
                    <FilterTitleIcon
                        className="menu-library-dish-filter-dialog__title-icon"
                        style={{ width: 32, height: 32, marginRight: 8, flexShrink: 0, color: '#7468f0' }}
                        aria-hidden
                    />
                ),
                onClose: () => state.closeDishFilterDialog(),
            }}
            footer={
                <Box className="menu-library-dish-filter-dialog__footer-inner">
                    <Button
                        type="button"
                        className="menu-library-dish-filter-dialog__btn-reset"
                        onClick={() => state.resetDishFilterDraft()}
                    >
                        {i18n.t('menu_library_filter_reset')}
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        className="menu-library-dish-filter-dialog__btn-apply"
                        onClick={() => state.applyDishFiltersFromDialog()}
                    >
                        {i18n.t('menu_library_filter_apply')}
                    </Button>
                </Box>
            }
        >
            <Box className="menu-library-dish-filter-dialog">
                <Box className="menu-library-dish-filter-dialog__quick-block">
                    <Typography component="p" className="menu-library-dish-filter-dialog__quick-hint">
                        {i18n.t('menu_library_filter_quick_hint')}
                    </Typography>
                    <Box className="menu-library-dish-filter-dialog__chips" role="group" aria-label={i18n.t('menu_library_filter_dialog_title')}>
                        {QUICK_CHIPS.map((c) => {
                            const active = state.dishFilterQuickPreset === c.id;
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    className={
                                        active
                                            ? 'menu-library-dish-filter-dialog__chip menu-library-dish-filter-dialog__chip--active'
                                            : 'menu-library-dish-filter-dialog__chip menu-library-dish-filter-dialog__chip--inactive'
                                    }
                                    onClick={() => state.setDishFilterQuickPreset(c.id)}
                                >
                                    {i18n.t(c.labelKey)}
                                </button>
                            );
                        })}
                    </Box>
                </Box>

                <Box className="menu-library-dish-filter-dialog__select-row">
                    <Box className="menu-library-dish-filter-dialog__select-field">
                        <Typography component="label" className="menu-library-dish-filter-dialog__select-label">
                            {i18n.t('menu_library_filter_label_category')}
                        </Typography>
                        <CommonInput
                            inputType="SELECT"
                            hiddenLabel
                            fullWidth
                            size="small"
                            value={state.dishFilterCategoryValue}
                            items={categoryItems}
                            onChange={(e) => state.setDishFilterCategoryValue(String((e.target as HTMLInputElement).value))}
                        />
                    </Box>
                    <Box className="menu-library-dish-filter-dialog__select-field">
                        <Typography component="label" className="menu-library-dish-filter-dialog__select-label">
                            {i18n.t('menu_library_filter_label_image')}
                        </Typography>
                        <CommonInput
                            inputType="SELECT"
                            hiddenLabel
                            fullWidth
                            size="small"
                            value={state.dishFilterImageValue}
                            items={imageItems}
                            onChange={(e) => state.setDishFilterImageValue(String((e.target as HTMLInputElement).value))}
                        />
                    </Box>
                    <Box className="menu-library-dish-filter-dialog__select-field">
                        <Typography component="label" className="menu-library-dish-filter-dialog__select-label">
                            {i18n.t('menu_library_filter_label_print')}
                        </Typography>
                        <CommonInput
                            inputType="SELECT"
                            hiddenLabel
                            fullWidth
                            size="small"
                            value={state.dishFilterPrintValue}
                            items={printItems}
                            onChange={(e) => state.setDishFilterPrintValue(String((e.target as HTMLInputElement).value))}
                        />
                    </Box>
                </Box>

                <Box className="menu-library-dish-filter-dialog__preview-block">
                    <Box className="menu-library-dish-filter-dialog__preview-head">
                        <Typography component="p" className="menu-library-dish-filter-dialog__preview-title">
                            {i18n.t('menu_library_filter_preview_title')}
                        </Typography>
                        <span className="menu-library-dish-filter-dialog__preview-count">
                            {String(i18n.t('menu_library_filter_preview_count')).replace(
                                /\{\{count\}\}/g,
                                String(previewRows.length),
                            )}
                        </span>
                    </Box>
                    <Box className="menu-library-dish-filter-dialog__table-wrap">
                        <table className="menu-library-dish-filter-dialog__table">
                            <thead>
                                <tr>
                                    <th className="menu-library-dish-filter-dialog__th menu-library-dish-filter-dialog__td-id">
                                        {i18n.t('menu_library_filter_col_id')}
                                    </th>
                                    <th className="menu-library-dish-filter-dialog__th menu-library-dish-filter-dialog__td-name">
                                        {i18n.t('menu_library_filter_col_name')}
                                    </th>
                                    <th className="menu-library-dish-filter-dialog__th menu-library-dish-filter-dialog__td-cat">
                                        {i18n.t('menu_library_filter_col_category')}
                                    </th>
                                    <th className="menu-library-dish-filter-dialog__th menu-library-dish-filter-dialog__td-price">
                                        {i18n.t('menu_library_filter_col_price')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {previewRows.map((row, index) => (
                                    <FilterPreviewRow
                                        key={getMenuLibraryDetailDishRowKey(
                                            row,
                                            index,
                                            state.isCloudMenuDetail,
                                        )}
                                        dish={row}
                                        state={state}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            </Box>
        </DialogComponent>
    );
});

const FilterPreviewRow = observer(function FilterPreviewRow({
    dish,
    state,
}: {
    dish: MenuDish;
    state: MenuLibraryManagementState;
}) {
    /** 第一行：名称1；第二行：菜品备注（与编辑弹窗 item_description 一致） */
    const nameLine1 = (dish.item_name1 || '').trim();
    const remarkLine = (dish.item_description ?? '').trim();
    const priceText =
        dish.item_type === 2
            ? '—'
            : `${state.getModuleCurrencyName()} ${dish.price_1 != null ? dish.price_1 : '—'}`;

    /** 云端新建 item_id 常为 0：优先本地编号，否则云端 string id */
    const idDisplay = getMenuLibraryFilterDishIdDisplay(dish, state.isCloudMenuDetail);

    return (
        <tr>
            <td
                className="menu-library-dish-filter-dialog__td menu-library-dish-filter-dialog__td-id"
                title={idDisplay.title}
            >
                {idDisplay.text}
            </td>
            <td className="menu-library-dish-filter-dialog__td menu-library-dish-filter-dialog__td-name">
                <div className="menu-library-dish-filter-dialog__name-line1">{nameLine1 || '—'}</div>
                {remarkLine ? (
                    <div className="menu-library-dish-filter-dialog__name-remark">{remarkLine}</div>
                ) : null}
            </td>
            <td className="menu-library-dish-filter-dialog__td menu-library-dish-filter-dialog__td-cat">
                {state.getDishTypeLabel(dish.item_type)}
            </td>
            <td className="menu-library-dish-filter-dialog__td menu-library-dish-filter-dialog__td-price">{priceText}</td>
        </tr>
    );
});

